import List from "@material-ui/core/esm/List";
import ListItem from "@material-ui/core/esm/ListItem";
import Box from "@material-ui/core/esm/Box";
import Tooltip from "@material-ui/core/esm/Tooltip";
import IconButton from "@material-ui/core/esm/IconButton";
import HelpOutlineSharp from "@material-ui/icons/esm/HelpOutlineSharp";
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValueLoadable } from 'recoil';
import getAgreementsAndVehicle from 'api/agreements/getAgreementsAndVehicle';
import Accordion from 'components/Accordion/Accordion';
import Spinner from 'components/Spinner/Spinner';
import { Subtitle, Title } from 'components/Text';
import EnumObeStatus from 'constants/obeStatuses';
import useAccordion from 'hooks/accordion/useAccordion';
import useCurrentCustomer from 'hooks/customer/useCurrentCustomer';
import AgreementComponent from 'modules/Agreements/AgreementsList/Agreement';
import { isAgreementStatusClosed } from 'utils/agreements/isAgreementInactive';
import checkLoadable from 'utils/recoil/checkLoadable';
import SearchService from 'utils/search/SearchService';
import useStyles from '../AgreementStyles';
export default function AgreementList({
  search,
  showClosedAgreements,
  showTerminatedObes
}) {
  const {
    t
  } = useTranslation();
  const {
    customer
  } = useCurrentCustomer();
  const {
    expanded,
    handleAccordionChange
  } = useAccordion();
  const {
    agreementsRoot,
    balanceInfoBody,
    balanceInfoHelper,
    tooltipText
  } = useStyles();
  const agreements = useRecoilValueLoadable(getAgreementsAndVehicle(customer?.id));
  const {
    contents,
    loading
  } = checkLoadable(agreements);
  const [filteredResults, setFilteredResults] = useState(undefined);
  const searchService = useMemo(() => new SearchService(contents ?? [], {
    keys: ['name', 'obes.obeNumber', 'obes.vehicle.registrationNumber', 'agreementNumber'],
    threshold: 0.0
  }), [contents]);
  useEffect(() => {
    const searchResult = searchService.search(search);
    setFilteredResults(searchResult);
  }, [search, searchService]);
  const results = filteredResults?.filter(result => showClosedAgreements || !isAgreementStatusClosed(result.status)).map(result => {
    return {
      ...result,
      obes: result.obes?.filter(obe => showTerminatedObes || obe.obeStatus !== EnumObeStatus.Terminated)
    };
  });
  const getBalance = agreement => {
    if (agreement?.accountBalance) {
      return `
      ${agreement?.accountBalance || ''}
      ${agreement?.accountBalance ? agreement?.accountCurrency : ''}`;
    }
    return '';
  };
  if (loading) {
    return /*#__PURE__*/React.createElement(Spinner, null);
  }
  if (contents?.length === 0) {
    return /*#__PURE__*/React.createElement(Box, {
      mt: 3
    }, /*#__PURE__*/React.createElement(Title, {
      align: "center"
    }, t('agreements.no_agreements')));
  }
  return /*#__PURE__*/React.createElement(Box, {
    mb: 3
  }, results?.some(result => result.accountBalance) && /*#__PURE__*/React.createElement("div", {
    className: balanceInfoBody
  }, /*#__PURE__*/React.createElement(Tooltip, {
    title: t('agreements.balance_helper_text') || "Balance refers to the agreement's outstanding/owed amount, including unpaid invoices",
    classes: {
      tooltip: tooltipText
    },
    placement: "top"
  }, /*#__PURE__*/React.createElement(IconButton, {
    "aria-label": "Balance helper text"
  }, /*#__PURE__*/React.createElement(HelpOutlineSharp, null))), /*#__PURE__*/React.createElement(Subtitle, {
    className: balanceInfoHelper
  }, t('agreements.balance'))), /*#__PURE__*/React.createElement(List, {
    className: agreementsRoot
  }, results?.map((agreement, index) => {
    const isItemExpanded = expanded === `panel${index}`;
    return /*#__PURE__*/React.createElement(ListItem, {
      disableGutters: true,
      key: agreement.id
    }, /*#__PURE__*/React.createElement(Accordion, {
      key: agreement.id,
      id: agreement?.id || '',
      title: agreement?.name || agreement.agreementNumber || '',
      expanded: isItemExpanded,
      onChange: () => handleAccordionChange(`panel${index}`),
      balance: getBalance(agreement)
    }, isItemExpanded && /*#__PURE__*/React.createElement(AgreementComponent, agreement)));
  }) ?? /*#__PURE__*/React.createElement(Title, {
    align: "center"
  }, t('agreements.no_agreements'))));
}